import React, { useEffect, useState, useRef, useMemo } from "react";
import { isMobile } from "../../../utils";
import debounce from "lodash/debounce";
import { ICON } from "../../../assets";
import UiIcon from "../UiIcon";
import "./UiSearch.css";

const UiSearch = ({ onSearch = () => { }, value = "", debounceDelay = 500, disabled = false, placeholder = "Rechercher", className = "", style = {}, variant = "default" }) => {
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [isShowSerchIcon, setShowSerchIcon] = useState(true);
  const [focused, isFocused] = useState(false);
  const searchInputRef = useRef(null);
  const shouldFocusRef = useRef(false);

  // Use a ref for onSearch to always use the latest callback
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Create stable debounced search function
  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      onSearchRef.current(searchValue);
    }, debounceDelay),
    [debounceDelay]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Restore focus when disabled becomes false if it was focused before
  useEffect(() => {
    if (!disabled && shouldFocusRef.current && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [disabled]);

  // Sync Parent -> Child
  // Only sync if not focused to avoid overwriting user typing with stale prop values
  useEffect(() => {
    const normalizedValue = value || "";
    if (normalizedValue !== searchTerm && !focused) {
      setSearchTerm(normalizedValue);
      setShowSerchIcon(normalizedValue === "");
    }
  }, [value, focused]);

  const handleChange = (event) => {
    if (disabled) return;
    const newVal = event.target.value;
    setShowSerchIcon(newVal === "");
    setSearchTerm(newVal);

    // Explicitly call the stable debounced function
    debouncedSearch(newVal);
  };

  const handleFocus = () => {
    if (!disabled && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleIconClick = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();

    if (!isShowSerchIcon) {
      setSearchTerm("");
      setShowSerchIcon(true);
      debouncedSearch.cancel(); // Cancel any pending debounced search
      onSearchRef.current(""); // Notification parent immediately for clear
    }
    handleFocus();
  };

  function keyDownHandler(event) {
    if (disabled) return;
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      debouncedSearch.cancel(); // Cancel pending debounce to avoid double calls
      onSearchRef.current(searchTerm);
    }
  }

  const isMobileVariant = variant === "mobile";

  return (
    <React.Fragment>
      <div
        className={`ui-search ui-search--${variant} ${disabled ? 'ui-search--disabled' : ''} ${className}`}
        onClick={handleFocus}
        style={{
          ...style,
          cursor: isMobileVariant ? (focused || !!searchTerm ? "normal" : "pointer") : style.cursor
        }}
      >
        <UiIcon
          className="ui-input-icon"
          icon={ICON.searchIcon}
          alt="search-icon"
          size="sm"
          onClick={handleFocus}
        />
        <input
          ref={searchInputRef}
          type={isMobileVariant ? "search" : "text"}
          placeholder={placeholder}
          value={searchTerm}
          onFocus={() => {
            isFocused(true);
            shouldFocusRef.current = true;
          }}
          onBlur={() => {
            // Only clear 'shouldFocus' if it was a real user blur, 
            // not a blur caused by disabling the input.
            if (!disabled) {
              isFocused(!!searchTerm);
              shouldFocusRef.current = false;
            }
          }}
          onChange={handleChange}
          onInput={handleChange} // Fallback for some search input behaviors
          className="ui-search-input"
          onKeyDown={keyDownHandler}
          disabled={disabled}
          style={{
            width: isMobileVariant && !disabled ? (focused || !!searchTerm ? (isMobile() ? "105px" : "300px") : "105px") : undefined
          }}
        />
        {!isShowSerchIcon && (
          <UiIcon
            className="ui-input-icon"
            icon={ICON.closeCrossIcon}
            alt="clear-icon"
            size="sm"
            onClick={handleIconClick}
            style={{ cursor: "pointer" }}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default UiSearch;
